/**
 * Centralized application configuration
 * All environment variables are accessed through this module
 */

import { logger } from '@core/logging';
import { LogCategory } from '@soteria/logger/browser';

export const config = {
  /**
   * API configuration
   */
  api: {
    /** Backend for Frontend URL */
    bffUrl: import.meta.env.VITE_BFF_URL,
    /** GraphQL API endpoint URL - derived from BFF URL */
    get graphqlUrl() {
      const bffUrl = this.bffUrl;

      if (!bffUrl) return undefined;

      // Remove trailing slash if present, then add /graphql
      return `${bffUrl.replace(/\/$/, '')}/graphql`;
    },
  },

  /**
   * External services configuration
   */
  externalServices: {
    /** Synesis Data Management URL - environment-specific external link */
    synesisUrl: import.meta.env.VITE_SYNESIS_URL,
  },

  /**
   * Monitoring and analytics configuration
   */
  monitoring: {
    /** Application Insights connection string */
    applicationInsights: import.meta.env.VITE_APPLICATIONINSIGHTS_CONNECTION_STRING,
  },

  /**
   * Environment information
   */
  env: {
    /** Current environment mode */
    mode: import.meta.env.MODE,
    /** Whether running in development mode */
    isDev: import.meta.env.DEV,
    /** Whether running in production mode */
    isProd: import.meta.env.PROD,
  },
} as const;

/**
 * Validate that all required environment variables are present
 * This should be called early in the application lifecycle
 */
export function validateConfig(): void {
  const requiredVars = [{ key: 'VITE_BFF_URL', value: config.api.bffUrl }];

  // Optional variables that are not required in development
  const optionalVars = [
    {
      key: 'VITE_APPLICATIONINSIGHTS_CONNECTION_STRING',
      value: config.monitoring.applicationInsights,
    },
  ];

  const missingVars = requiredVars.filter(({ value }) => !value);

  if (missingVars.length > 0) {
    const missing = missingVars.map(({ key }) => key).join(', ');

    throw new Error(`Missing required environment variables: ${missing}`);
  }

  // Validate that GraphQL URL can be derived
  if (!config.api.graphqlUrl) {
    throw new Error('Unable to derive GraphQL URL from BFF URL');
  }

  // Log info about optional variables that are not set
  const missingOptionalVars = optionalVars.filter(({ value }) => !value);

  if (missingOptionalVars.length > 0) {
    const missing = missingOptionalVars.map(({ key }) => key).join(', ');

    logger.debug(`Optional environment variables not set: ${missing}`, {
      category: LogCategory.SYSTEM,
      missingVariables: missing,
    });
  }
}
